import React, { createContext, useContext, ReactNode } from 'react';
import { IStreamingProviderFactory } from '~/packages/core';

// Context to hold the Streaming Provider Factory
interface StreamingProviderContextValue {
    streamingProviderFactory: IStreamingProviderFactory | null;
}

// Create the context with initial value
const StreamingProviderContext = createContext<StreamingProviderContextValue>({
    streamingProviderFactory: null
});

// Provider props interface
export interface StreamingProviderProps {
    streamingProviderFactory: IStreamingProviderFactory;
    children: ReactNode;
}

/**
 * Provider component for StreamingProvider.
 * Makes the streaming provider factory available to all child components.
 */
export const StreamingProvider: React.FC<StreamingProviderProps> = ({
    streamingProviderFactory,
    children
}) => {
    return (
        <StreamingProviderContext.Provider value={{ streamingProviderFactory }}>
            {children}
        </StreamingProviderContext.Provider>
    );
};

/**
 * Hook to access the StreamingProviderFactory from the context.
 * @returns The StreamingProviderFactory instance
 * @throws Error if used outside of a StreamingProvider
 */
export const useStreamingProviderFactory = (): IStreamingProviderFactory => {
    const { streamingProviderFactory } = useContext(StreamingProviderContext);

    if (!streamingProviderFactory) {
        throw new Error(
            'useStreamingProviderFactory must be used within a StreamingProvider'
        );
    }

    return streamingProviderFactory;
};
